import { Injectable } from "@angular/core";
import { HttpClient, HttpHeaders, HttpParams } from "@angular/common/http";
import { ConfigService } from "./config.service";
import { Observable } from "rxjs";
import {
  CreateTasksRequest,
  SummarizeTicketRequest,
  TicketCreateTasksResult,
  TicketSummarizeResult,
} from "../../models/ai-command.model";

/**
 * Client for the Chronodesk AI Command API (chronodesk-ai repo).
 *
 * Mirrors the FormsApiService house style: base URL comes from ConfigService,
 * every call carries the company-name header, and the JWT bearer token is
 * attached automatically by JwtInterceptor (which is wired to recognise this
 * host and use the 'Bearer ' scheme).
 *
 * This is the shared entry point for all future AI command functionality;
 * add a new public method per command.
 */
@Injectable()
export class AiCommandService {

  private get aiCommandApiUrl(): string {
    return this.cs.aiCommandApiEndPoint;
  }

  constructor(
    private http: HttpClient,
    private cs: ConfigService
  ) { }

  private getCompanyName(): string {
    const url = window.location.href;
    const urlParts = url.split("/");
    const printMatch = url.match(/print/g);
    const cMatch = url.includes("/c/");
    const positionToExtract = (printMatch && printMatch.length > 1) || cMatch ? 4 : 3;
    if (urlParts.length > positionToExtract) {
      return urlParts[positionToExtract] || "";
    }
    return "";
  }

  private jsonHeaders(): HttpHeaders {
    return new HttpHeaders({
      "Content-Type": "application/json",
      "company-name": this.getCompanyName(),
    });
  }

  private post<T>(path: string, body: any): Observable<T> {
    return this.http.post<T>(
      this.aiCommandApiUrl + path,
      JSON.stringify(body ?? {}),
      { headers: this.jsonHeaders() }
    );
  }

  // ============================================
  // Ticket commands
  // ============================================

  /** Summarise a ticket. Returns the AI summary (display only; the main app owns persisting it). */
  summarizeTicket(ticketId: number, body?: SummarizeTicketRequest): Observable<TicketSummarizeResult> {
    return this.post<TicketSummarizeResult>(`/api/tickets/${ticketId}/summarize`, body ?? {});
  }

  /** Propose Activity tasks from a ticket. Returns proposals; the main app owns creating the Activities. */
  createTasksFromTicket(ticketId: number, body: CreateTasksRequest): Observable<TicketCreateTasksResult> {
    return this.post<TicketCreateTasksResult>(`/api/tickets/${ticketId}/create-tasks`, body ?? {});
  }

  /** Propose Activity tasks from a single ticket email (smaller, more focused than the whole ticket). */
  createTasksFromEmail(ticketId: number, mailMessageId: number, body?: CreateTasksRequest): Observable<TicketCreateTasksResult> {
    return this.post<TicketCreateTasksResult>(`/api/tickets/${ticketId}/mail/${mailMessageId}/create-tasks`, body ?? {});
  }
}
